import { 
  Shield, 
  Building, 
  FileText, 
  Users, 
  AlertTriangle, 
  Settings,
  MessageSquare,
  Wrench,
  BarChart3,
  ClipboardList,
  Award,
  Target,
  Home,
  Bell,
  Network,
  UserCheck,
  Truck,
  Smartphone,
  Package,
  MapPin,
  Cog,
  Key,
  ConciergeBell,
  Star,
  Clock,
  Zap
} from 'lucide-react';
import { UserRole } from '@/types';

export interface NavigationItem {
  title: string;
  icon: any;
  route: string;
  roles: UserRole[];
  mvpActive?: boolean;
}

export interface NavigationGroup {
  title: string;
  icon: any;
  roles: UserRole[];
  items: NavigationItem[];
  mvpActive?: boolean;
}

// Standalone items (no dropdown)
export const standaloneItems: NavigationItem[] = [
  {
    title: 'Operations Center',
    icon: Target,
    route: '/operations',
    roles: ['admin', 'super_admin', 'manager', 'maintenance', 'vendor', 'resident', 'tenant', 'insurance_adjuster', 'lender', 'maintenance_supervisor', 'maintenance_coordinator', 'vendor_admin', 'vendor_manager', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
    mvpActive: true
  }
];

// Compliance as standalone item (positioned after Communication Hub)
export const lossControlItem: NavigationItem[] = [
  {
    title: 'Compliance',
    icon: Award,
    route: '/loss-control',
    roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'resident', 'tenant', 'insurance_adjuster', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
    mvpActive: false
  }
];

// Grouped items with dropdowns
export const navigationGroups: NavigationGroup[] = [
  {
    title: 'Maintenance Ops',
    icon: Building,
    roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'resident', 'tenant', 'maintenance_supervisor', 'maintenance_coordinator', 'vendor_admin', 'vendor_manager', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
    mvpActive: true,
    items: [
      {
        title: 'Properties',
        icon: Building,
        route: '/properties',
        roles: ['admin', 'super_admin', 'manager', 'maintenance', 'insurance_adjuster', 'lender', 'maintenance_supervisor', 'maintenance_coordinator', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      },
      {
        title: 'Work Orders',
        icon: Wrench,
        route: '/work-orders',
        roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'maintenance_supervisor', 'maintenance_coordinator', 'vendor_admin', 'vendor_manager', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      },
      {
        title: 'Tech Management',
        icon: Users,
        route: '/tech-management',
        roles: ['admin', 'super_admin', 'manager', 'maintenance_supervisor', 'maintenance_coordinator', 'vendor_admin', 'vendor_manager', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      },
      {
        title: 'Vendor Network',
        icon: Network,
        route: '/vendors',
        roles: ['admin', 'super_admin', 'manager', 'maintenance', 'maintenance_supervisor', 'maintenance_coordinator', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: false
      },
      {
        title: 'Analytics & AI',
        icon: BarChart3,
        route: '/analytics-ai',
        roles: ['admin', 'super_admin', 'manager', 'maintenance', 'maintenance_supervisor', 'maintenance_coordinator', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: false
      },
      {
        title: 'Payroll & Hours',
        icon: Clock,
        route: '/payroll-hours',
        roles: ['admin', 'super_admin', 'manager', 'maintenance_supervisor', 'maintenance_director', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: false
      }
    ]
  },
  {
    title: 'Tenant',
    icon: Smartphone,
    roles: ['admin', 'super_admin', 'manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
    mvpActive: false,
    items: [
      {
        title: 'Tenant Portal',
        icon: Smartphone,
        route: '/tenant-engagement',
        roles: ['admin', 'super_admin', 'manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: false
      },
      {
        title: 'Concierge',
        icon: ConciergeBell,
        route: '/concierge-dashboard',
        roles: ['admin', 'super_admin', 'manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: false
      }
    ]
  },
  {
    title: 'Communication Hub',
    icon: MessageSquare,
    roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'resident', 'tenant', 'insurance_adjuster', 'lender', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
    mvpActive: true,
    items: [
      {
        title: 'Communications',
        icon: MessageSquare,
        route: '/communications',
        roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'resident', 'tenant', 'insurance_adjuster', 'lender', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      },
      {
        title: 'Alerts',
        icon: Bell,
        route: '/alerts',
        roles: ['admin', 'super_admin', 'manager', 'vendor', 'maintenance', 'resident', 'tenant', 'insurance_adjuster', 'lender', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      },
      {
        title: 'Emergency Plans',
        icon: FileText,
        route: '/emergency-plans',
        roles: ['admin', 'super_admin', 'manager', 'maintenance', 'executive_maintenance_director', 'regional_maintenance_director', 'site_maintenance_manager', 'executive_property_manager', 'regional_property_manager', 'property_manager'],
        mvpActive: true
      }
    ]
  },
  {
    title: 'Risk & Insurance',
    icon: Shield,
    roles: ['admin', 'super_admin', 'manager', 'insurance_adjuster', 'lender', 'executive_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
    mvpActive: true,
    items: [
      {
        title: 'Insurance Optimization',
        icon: Target,
        route: '/insurance-optimization',
        roles: ['admin', 'super_admin', 'manager', 'insurance_adjuster', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: true
      },
      {
        title: 'Package Builder',
        icon: Package,
        route: '/insurance-optimization/package-builder',
        roles: ['admin', 'super_admin', 'manager', 'executive_property_manager'],
        mvpActive: true
      },
      {
        title: 'Lender Risk Assessment',
        icon: Target,
        route: '/lender-risk-assessment',
        roles: ['admin', 'super_admin', 'manager', 'lender', 'executive_property_manager'],
        mvpActive: false
      },
      {
        title: 'Risk Mapping',
        icon: Target,
        route: '/risk-mapping',
        roles: ['admin', 'super_admin', 'manager', 'lender', 'executive_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: false
      }
    ]
  },
  {
    title: 'Reports & Analytics',
    icon: BarChart3,
    roles: ['admin', 'manager', 'super_admin', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
    mvpActive: false,
    items: [
      {
        title: 'Loss Prevention Analytics',
        icon: Shield,
        route: '/analytics',
        roles: ['admin', 'manager', 'super_admin', 'executive_maintenance_director', 'executive_property_manager'],
        mvpActive: false
      },
      {
        title: 'Reports',
        icon: BarChart3,
        route: '/reports',
        roles: ['admin', 'manager', 'super_admin', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: false
      },
      {
        title: 'Analytics Engine',
        icon: Target,
        route: '/analytics-engine',
        roles: ['admin', 'manager', 'super_admin', 'executive_maintenance_director', 'executive_property_manager'],
        mvpActive: false
      },
      {
        title: 'Claims Dashboard',
        icon: ClipboardList,
        route: '/insurance',
        roles: ['admin', 'manager', 'insurance_adjuster', 'super_admin', 'executive_property_manager'],
        mvpActive: false
      },
      {
        title: 'Loss Management',
        icon: Shield,
        route: '/losses',
        roles: ['admin', 'manager', 'insurance_adjuster', 'super_admin', 'executive_maintenance_director', 'executive_property_manager'],
        mvpActive: false
      },
      {
        title: 'Feedback Analytics',
        icon: Star,
        route: '/feedback-analytics',
        roles: ['admin', 'manager', 'super_admin', 'executive_maintenance_director', 'regional_maintenance_director', 'executive_property_manager', 'regional_property_manager'],
        mvpActive: false
      }
    ]
  }
];

// Admin-only standalone items
export const adminItems: NavigationItem[] = [
  {
    title: 'Settings',
    icon: Settings,
    route: '/settings',
    roles: ['admin', 'super_admin'],
    mvpActive: true
  }
];

// Admin Tools group
export const adminToolsGroup: NavigationGroup = {
  title: 'Admin Tools',
  icon: Cog,
  roles: ['admin', 'super_admin'],
  items: [
    {
      title: 'Admin Hub',
      icon: Cog,
      route: '/admin',
      roles: ['admin', 'super_admin']
    },
    {
      title: 'API Portal',
      icon: Key,
      route: '/api-portal',
      roles: ['admin', 'super_admin']
    },
    {
      title: 'Indoor Positioning',
      icon: MapPin,
      route: '/admin/indoor-positioning',
      roles: ['admin', 'super_admin']
    },
    {
      title: 'Founder Command',
      icon: Zap,
      route: '/admin/founder-command',
      roles: ['admin', 'super_admin']
    }
  ]
};

// Legacy exports for backward compatibility
export const mainNavItems: NavigationItem[] = standaloneItems;
export const managementNavItems: NavigationItem[] = [];
export const adminNavItems: NavigationItem[] = adminItems;
